import { Card, CardBody, Col, Container, Row, Spinner, Alert, Button, Badge } from 'reactstrap';
import { Fragment } from 'react';
import React, { useState, useEffect } from 'react';
import { Breadcrumbs, H4, H5, P, Image } from '../../../../AbstractElements';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2 } from 'react-feather';
import eventApi from '../../../../api/CheckInEvent';
import CreateEventModal from './CreateEventModal';
import EditEventModal from './EditEventModal';

const EventCheckinManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const navigate = useNavigate();

  // Load events from API
  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await eventApi.getAll();
      
      // Parse API response format
      let eventsData = [];
      if (response && response.success && response.data && response.data.events) {
        eventsData = response.data.events;
      } else if (Array.isArray(response)) {
        eventsData = response;
      } else if (response && Array.isArray(response.data)) {
        eventsData = response.data;
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        eventsData = [];
      }
      
      setEvents(eventsData);
    } catch (err) {
      console.error('❌ Error loading events:', err);
      setError('Không thể tải danh sách sự kiện');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleEventClick = (eventId) => {
    navigate(`${process.env.PUBLIC_URL}/app/event/event-detail/${eventId}`);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'now':
        return <Badge color="success">Đang diễn ra</Badge>;
      case 'ended':
        return <Badge color="secondary">Đã kết thúc</Badge>;
      case 'upcoming':
        return <Badge color="info">Sắp diễn ra</Badge>;
      case 'unknown':
        return <Badge color="warning">Chưa xác định</Badge>;
      default:
        return <Badge color="warning">Chưa xác định</Badge>;
    }
  };

  const computeEventStatus = (event) => {
    if (!event) return 'unknown';

    const { startTime, endTime } = event;
    const fallbackStatus = event.status || 'unknown';

    if (!startTime || !endTime) {
      return fallbackStatus;
    }

    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const now = Date.now();

    if (Number.isNaN(start) || Number.isNaN(end)) {
      return fallbackStatus;
    }

    if (now < start) return 'upcoming';
    if (now >= start && now <= end) return 'now';
    return 'ended';
  };

  const getEventImage = (event) => {
    // Check for new API format: event.img[0].url
    if (event.img && Array.isArray(event.img) && event.img.length > 0) {
      return event.img[0].url;
    }
    
    // Check for old format: eventImages
    if (event.eventImages && event.eventImages.length > 0) {
      return event.eventImages[0];
    }
    
    // Check for old format: descriptionImages
    if (event.descriptionImages && event.descriptionImages.length > 0) {
      return event.descriptionImages[0];
    }
    
    return 'https://via.placeholder.com/300x200/6c757d/ffffff?text=No+Image';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Chưa xác định';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('vi-VN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC'
      }).format(date);
    } catch (error) {
      console.warn('⚠️ Unable to format date:', error);
      return 'Chưa xác định';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Chưa xác định';
    try {
      const date = new Date(timeString);
      return new Intl.DateTimeFormat('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      }).format(date);
    } catch (error) {
      console.warn('⚠️ Unable to format time:', error);
      return 'Chưa xác định';
    }
  };

  const formatDateTime = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Chưa xác định';
    try {
      const startTimeStr = formatTime(startTime);
      const endTimeStr = formatTime(endTime);
      return `${startTimeStr} - ${endTimeStr}`;
    } catch (error) {
      return 'Chưa xác định';
    }
  };

  // Toggle create modal
  const toggleCreateModal = () => {
    setCreateModal(!createModal);
  };

  const closeEditModal = () => {
    setEditModal(false);
    setSelectedEvent(null);
  };

  const handleEditClick = (event) => {
    setSelectedEvent(event);
    setEditModal(true);
  };

  // Handle event created successfully
  const handleEventCreated = () => {
    loadEvents(); // Reload events list
  };

  const handleEventUpdated = () => {
    loadEvents();
    closeEditModal();
  };

  // Show loading state
  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent='Events' title='Danh sách sự kiện' mainTitle='Danh sách sự kiện' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Đang tải danh sách sự kiện...</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  // Show error state
  if (error) {
    return (
      <Fragment>
        <Breadcrumbs parent='Events' title='Danh sách sự kiện' mainTitle='Danh sách sự kiện' />
        <Container fluid={true}>
          <div className="py-5">
            <Alert color="danger">
              <strong>Lỗi:</strong> {error}
            </Alert>
          </div>
        </Container>
      </Fragment>
    );
  }

  // Show no events state
  if (events.length === 0) {
    return (
      <Fragment>
        <Breadcrumbs parent='Events' title='Danh sách sự kiện' mainTitle='Danh sách sự kiện' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <p>Không có sự kiện nào</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs parent='Events' title='Danh sách sự kiện' mainTitle='Danh sách sự kiện' />
      <Container fluid={true}>
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <H4></H4>
            <Button color="primary" onClick={toggleCreateModal}>
              <Plus size={16} className="me-1" />
              Tạo sự kiện mới
            </Button>
          </Col>
        </Row>

        <div className="events-grid">
          <Row>
            {events.map((event) => {
              const status = computeEventStatus(event);
              return (
                <Col key={event.id} xl="4" lg="6" md="6" sm="12" className="mb-4">
                  <Card className="event-card h-100" style={{ cursor: 'pointer' }} onClick={() => handleEventClick(event.id)}>
                    <div className="event-image-container" style={{ height: '200px', overflow: 'hidden' }}>
                      <Image
                        attrImage={{
                          src: getEventImage(event),
                          alt: event.title || 'Event Image',
                          className: 'img-fluid w-100',
                          style: { 
                            height: '100%', 
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease'
                          },
                          onError: (e) => {
                            e.target.src = 'https://via.placeholder.com/400x200/6c757d/ffffff?text=No+Image';
                          }
                        }}
                      />
                    </div>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <H5 className="mb-1">{event.title || 'Sự kiện'}</H5>
                        {getStatusBadge(status)}
                      </div>
                      
                      <P className="text-muted small mb-2">
                        <i className="fa fa-calendar me-1"></i>
                        {formatDate(event.date)}
                      </P>
                      
                      {event.startTime && event.endTime && (
                        <P className="text-muted small mb-2">
                          <i className="fa fa-clock me-1"></i>
                          {formatDateTime(event.startTime, event.endTime)}
                        </P>
                      )}
                      
                      <P className="text-muted small mb-2">
                        <i className="fa fa-map-marker me-1"></i>
                        {event.location || 'Chưa xác định'}
                      </P>
                      
                      <P className="text-muted small mb-3">
                        <i className="fa fa-user me-1"></i>
                        {event.organizer || 'Chưa xác định'}
                      </P>
                    
                    <div className="d-flex flex-column gap-2">
                      <span className="badge bg-light text-dark mb-0 align-self-start">
                        <i className="fa fa-users me-1"></i>
                        {event.maxAttendees || 'Không giới hạn'} người
                      </span>
                      <div className="d-grid gap-2">
                        <Button
                          color="secondary"
                          size="sm"
                          outline
                          className="w-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(event);
                          }}
                        >
                          <Edit2 size={14} className="me-1" />
                          Chỉnh sửa
                        </Button>
                        <Button 
                          color="primary" 
                          size="sm"
                          className="w-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event.id);
                          }}
                        >
                          Xem chi tiết
                        </Button>
                      </div>
                    </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>

        {/* Modal Tạo sự kiện mới */}
        <CreateEventModal
          isOpen={createModal}
          toggle={toggleCreateModal}
          onEventCreated={handleEventCreated}
        />

        {/* Modal Chỉnh sửa sự kiện */}
        <EditEventModal
          isOpen={editModal}
          toggle={closeEditModal}
          event={selectedEvent}
          onEventUpdated={handleEventUpdated}
        />
      </Container>
    </Fragment>
  );
};

export default EventCheckinManagement;
